import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Clock, RefreshCw, Loader2, AlertTriangle, Zap, Layers,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  fmt, tierStyle, statusBadge, biasStyle, factorLabels, Metric, TradeSection, TradeCard, ZoneReasonCard, ErrorCard,
} from "@/components/AuraTradeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 5 * 60 * 1000; // 5 min

export default function DailySetups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [allTrackRecord, setAllTrackRecord] = useState(null);
  const [breakouts, setBreakouts] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [{ data }, allTrackResp, breakoutResp] = await Promise.all([
        axios.get(`${API}/aura/intraday/all`),
        axios.get(`${API}/aura/intraday/track-record/all?limit=12`).catch(() => ({ data: null })),
        axios.get(`${API}/aura/intraday/breakouts`).catch(() => ({ data: null })),
      ]);
      setData(data);
      setAllTrackRecord(allTrackResp.data || null);
      setBreakouts(breakoutResp.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) toast.error("Failed to load intraday zones — retrying shortly");
      setTimeout(() => fetchAll(true), 8000);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => fetchAll(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="daily-setups-loading">
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-72" />)}
        </div>
      </div>
    );
  }

  const dailyMeta = data?.daily_trade;
  const hasInstruments = (data?.instruments || []).length > 0;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="daily-setups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Daily Setups
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            High-confluence entry zones · Analysis after Asian session close · UK time
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => fetchAll(false)}
            disabled={refreshing}
            data-testid="refresh-setups-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Rescan Now
          </Button>
        </div>
      </div>

      {/* Trade of the Day - hottest, strongest-confluence pick */}
      {dailyMeta && (
        <TradeSection meta={dailyMeta} label="Trade of the Day" horizon="day" testidBase="trade-of-the-day"
          subtitle="Direction locked in by 8-9am UK (London open) from the strongest confluence - wait for price to actually reach the buy/sell zone before acting." />
      )}

      {/* Breakout Setups - separate engine: trend-aligned breaks, not pullbacks */}
      {breakouts && <BreakoutSetups data={breakouts} />}

      {/* Empty / error state */}
      {!hasInstruments && (
        <Card data-testid="daily-setups-empty" className="border-slate-700/50">
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500/70" />
            <div className="text-sm text-muted-foreground">
              Couldn't load market data. This can happen if yfinance is rate-limited.
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchAll(false)} data-testid="daily-setups-retry-btn">
              <RefreshCw className="w-3 h-3 mr-2" />
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Other assets, inline with score alongside their buy/sell zones */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">All Assets - Buy/Sell Zones &amp; Score</div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {(data?.instruments || []).map((inst) => (
            <DailyInstrumentCard key={inst.symbol} instrument={inst} />
          ))}
        </div>
      </div>

      {/* All-assets win-rate log + self-tuning status */}
      {allTrackRecord && <AllAssetsPerformance data={allTrackRecord} />}

      <p className="text-xs text-muted-foreground text-center pt-4">
        Zones auto-refresh every 5 minutes while this page is open. For informational purposes only — not financial advice.
      </p>
    </div>
  );
}

function AllAssetsPerformance({ data }) {
  const [tab, setTab] = useState("day");
  const bucket = data[tab] || { per_asset: {}, overall: {}, by_factor: {} };
  const tuning = data.tuning || {};
  const changes = Object.entries(tuning.changes || {});
  const rateColor = (r) => r == null ? "text-slate-400" : r >= 55 ? "text-emerald-400" : r >= 40 ? "text-amber-400" : "text-red-400";

  return (
    <Card className="border-slate-700/50" data-testid="all-assets-performance">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="w-4 h-4 text-cyan-400" /> All-Assets Win-Rate Log
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              How every AuraTrade signal has actually performed, per asset and per zone type - feeds the weekly self-tuning below.
            </p>
          </div>
          <div className="flex gap-2">
            {["day", "week"].map((k) => (
              <Button key={k} size="sm" variant={tab === k ? "default" : "outline"}
                onClick={() => setTab(k)} data-testid={`all-perf-tab-${k}`}>
                {k === "day" ? "Trade of the Day" : "Trade of the Week"}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Overall aggregate across all 8 assets */}
        <div className="flex flex-wrap gap-5 pb-3 border-b border-slate-700/50" data-testid={`all-perf-overall-${tab}`}>
          <Metric label="Sample" value={bucket.overall?.sample_size ?? 0} sub="across all assets" color="text-slate-200" />
          <Metric label="Win Rate" value={bucket.overall?.win_rate_pct != null ? `${bucket.overall.win_rate_pct}%` : "—"}
            sub={`${bucket.overall?.win_count ?? 0}W / ${bucket.overall?.loss_count ?? 0}L`} color={rateColor(bucket.overall?.win_rate_pct)} />
          <Metric label="Cumulative" value={`${(bucket.overall?.cumulative_r ?? 0) > 0 ? "+" : ""}${bucket.overall?.cumulative_r ?? 0}R`}
            sub={`Avg ${bucket.overall?.avg_r ?? "—"}R`} color={(bucket.overall?.cumulative_r ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"} />
        </div>

        {/* Per-asset breakdown */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Per Asset</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`all-perf-per-asset-${tab}`}>
            {Object.entries(bucket.per_asset || {}).map(([sym, a]) => (
              <div key={sym} className="bg-slate-800/40 rounded-lg p-2.5 text-xs">
                <div className="font-medium truncate">{a.label}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className={rateColor(a.stats?.win_rate_pct)}>
                    {a.stats?.win_rate_pct != null ? `${a.stats.win_rate_pct}%` : "—"}
                  </span>
                  <span className="text-muted-foreground">{a.stats?.sample_size ?? 0} trades</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Factor/POI type breakdown */}
        {Object.keys(bucket.by_factor || {}).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">By Zone Type</div>
            <div className="space-y-2" data-testid={`all-perf-by-factor-${tab}`}>
              {Object.entries(bucket.by_factor).map(([poi, s]) => (
                <div key={poi} className="flex items-center gap-3 text-xs">
                  <span className="w-28 shrink-0 text-muted-foreground">{poi}</span>
                  <Progress value={s.win_rate_pct ?? 0} className="h-1.5 flex-1" />
                  <span className={`w-24 text-right ${rateColor(s.win_rate_pct)}`}>
                    {s.win_rate_pct != null ? `${s.win_rate_pct}%` : "—"} ({s.sample_size})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Self-tuning status */}
        <div className="text-[11px] text-muted-foreground pt-2 border-t border-slate-700/50" data-testid="factor-tuning-status">
          {tuning.last_tuned_at
            ? <>
                <span className="text-cyan-400 font-medium">Self-tuning active</span> — last nudged {new Date(tuning.last_tuned_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}.
                {changes.length > 0 && (
                  <span> {changes.map(([k, c]) => `${factorLabels[k] || k} ${c.new_multiplier > c.old_multiplier ? "↑" : "↓"} (${c.win_rate_pct}% win, ${c.sample_size} trades)`).join(" · ")}</span>
                )}
              </>
            : "Self-tuning runs automatically every Saturday once there's enough completed-trade history per zone type (min 6 filled trades)."}
        </div>
      </CardContent>
    </Card>
  );
}

function BreakoutSetups({ data }) {
  const top = data.top_breakout;
  const list = (data.instruments || []).filter((i) => i.breakout);
  if (list.length === 0) {
    return (
      <Card className="border-slate-700/50" data-testid="breakout-setups-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" /> Breakout Setups
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Trend-aligned breaks of structure on 15m/1H/4H/Daily/Weekly - separate from the pullback zone trades above.
          </p>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">No fresh, trend-aligned breakout on any asset right now.</CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-cyan-500/20" data-testid="breakout-setups">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-400" /> Breakout Setups
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Trend-aligned breaks of structure on 15m/1H/4H/Daily/Weekly - separate from the pullback zone trades above.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.map((i) => {
          const b = i.breakout;
          const isTop = top && b.symbol === top.symbol && b.timeframe === top.timeframe;
          return (
            <div key={i.symbol} className={`rounded-lg p-2.5 text-xs flex items-center gap-3 ${isTop ? "bg-cyan-500/10 border border-cyan-500/30" : "bg-slate-800/40"}`}
              data-testid={`breakout-row-${i.symbol}`}>
              {isTop && <Badge className="bg-cyan-500/30 text-cyan-200 border-cyan-400/50">TOP</Badge>}
              <span className="font-medium w-32 truncate">{i.label}</span>
              <Badge variant="outline" className={b.direction === "LONG" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}>
                {b.direction}
              </Badge>
              <span className="text-muted-foreground">{b.timeframe} break</span>
              <span className="text-muted-foreground">Entry {fmt(b.entry, i.symbol)}</span>
              <span className="ml-auto text-muted-foreground">R:R {b.rr_ratio}:1{b.rr_ratio_2 ? ` (partial ${b.rr_ratio_2}:1)` : ""}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function DailyInstrumentCard({ instrument: inst }) {
  if (inst.error) return <ErrorCard label={inst.label} symbol={inst.symbol} />;

  const buyZone = (inst.zones || []).find((z) => z.side === "support");
  const sellZone = (inst.zones || []).find((z) => z.side === "resistance");
  const otherZones = (inst.zones || []).filter((z) => z !== buyZone && z !== sellZone);
  const inZone = inst.in_zone_now;
  const armed = inZone?.status === "ENTRY_ARMED";
  const htf = inst.htf_bias;

  return (
    <Card
      className={`transition-all hover:border-amber-500/40 ${armed ? "border-emerald-400/60 shadow-emerald-400/20 shadow-lg" : inZone ? "border-emerald-500/40 shadow-emerald-500/10 shadow-lg" : ""}`}
      data-testid={`instrument-card-${inst.symbol}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {inst.label}
              </CardTitle>
              {inst.is_proxy && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-amber-400 border-amber-500/40" title={`Data proxied from ${inst.data_source}`}>
                  PROXY
                </Badge>
              )}
              {htf?.combined && (
                <Badge
                  variant="outline"
                  className={`text-[9px] px-1.5 py-0 ${biasStyle[htf.combined] || biasStyle.NEUTRAL}`}
                  title={`Monthly ${htf.monthly} · Weekly ${htf.weekly} · Daily ${htf.daily}`}
                  data-testid={`htf-bias-${inst.symbol}`}
                >
                  HTF {htf.combined}
                </Badge>
              )}
            </div>
            <div className="text-2xl font-semibold mt-1">{fmt(inst.current_price, inst.symbol)}</div>
          </div>
          {inZone && (
            <Badge className={armed ? statusBadge.ENTRY_ARMED.className : statusBadge.IN_ZONE.className} data-testid={`zone-status-${inst.symbol}`}>
              <Zap className="w-3 h-3 mr-1" />
              {armed ? "ENTRY ARMED" : "IN ZONE"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Ranging market - no trade shown by design (quality over quantity) */}
        {inst.is_ranging && (
          <div className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid={`ranging-badge-${inst.symbol}`}>
            <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/40">RANGING</Badge>
            No clear higher-timeframe trend - no Daily trade surfaced for this asset right now.
          </div>
        )}

        {inst.trade_of_the_day ? (
          /* Full senior-trader treatment: this asset has a real Entry/Stop/Target -
             Confluence Breakdown + Senior Trader's Take + live chart, same as the
             Trade of the Day headline. */
          <TradeCard trade={inst.trade_of_the_day} label={`${inst.label} - Daily Setup`} testid={`daily-instrument-trade-${inst.symbol}`} />
        ) : (
          <>
            {/* Nothing set up yet for this asset - show the zones being watched instead */}
            <ZoneReasonCard zone={buyZone} symbol={inst.symbol} kind="buy" />
            <div className="border-t border-slate-700/50" />
            <ZoneReasonCard zone={sellZone} symbol={inst.symbol} kind="sell" />

            {/* No confirmed zone but confluences exist without FVG displacement */}
            {!buyZone && !sellZone && inst.unconfirmed_confluence_count > 0 && (
              <div className="text-[11px] text-muted-foreground italic" data-testid={`no-fvg-note-${inst.symbol}`}>
                {inst.unconfirmed_confluence_count} confluence zone{inst.unconfirmed_confluence_count > 1 ? "s" : ""} found, but none yet confirmed by a Fair Value Gap displacement — waiting for an institutional footprint.
              </div>
            )}
          </>
        )}

        {/* Other POIs beyond the top buy/sell zone */}
        {otherZones.length > 0 && (
          <div className="pt-2 border-t border-slate-700/50 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Other POIs</div>
            {otherZones.slice(0, 3).map((z, i) => (
              <div key={i} className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">{z.poi_type} <span className="opacity-60">({z.side})</span></span>
                <span>{fmt(z.center, inst.symbol)}</span>
                <span className="text-slate-400">{z.conviction.score_pct}%</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
